import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Bold, Italic, Underline, List, Link as LinkIcon, Send, Loader2, Paperclip, CheckCircle2, FileText, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { availableDocs, buildAttachments } from '../../utils/paperworkPdfs';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  try {
    const a = JSON.parse(localStorage.getItem('dhs-agent') || 'null') || JSON.parse(localStorage.getItem('dhs-admin') || 'null');
    return a?.token ? { Authorization: `Bearer ${a.token}` } : {};
  } catch { return {}; }
};

/**
 * Central handler for 401 responses on staff endpoints. The old flow silently
 * surfaced "Token invalid or expired" as a red toast which agents mistook for
 * a Resend-side failure. We now clear the stale session in localStorage and
 * bounce the user back to the correct login screen — that way they instantly
 * see WHY it failed and get a working session on their next click.
 */
const handleAuthFailure = (toast) => {
  const hadAgent = !!localStorage.getItem('dhs-agent');
  const hadAdmin = !!localStorage.getItem('dhs-admin');
  localStorage.removeItem('dhs-agent');
  localStorage.removeItem('dhs-admin');
  toast({
    title: 'Session expired',
    description: 'Please sign in again to send this email — redirecting to the login page…',
  });
  const to = hadAdmin ? '/admin/login' : hadAgent ? '/agent/login' : '/login';
  setTimeout(() => { window.location.href = to; }, 1500);
};

const parseAddrs = (s) => (s || '').split(/[,;\n]/).map((x) => x.trim().toLowerCase()).filter(Boolean);
const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

/**
 * Full-screen email composer with a WYSIWYG rich-text editor over the designed
 * HTML template. Editable via document.designMode inside an iframe so all
 * inline styles/tables/branding are preserved while the user types.
 */
const EmailComposer = ({ open, onClose, policy, templateKind, initialSubject, initialHtml, initialText, defaultAttachmentKeys, tag }) => {
  const { toast } = useToast();
  const isCancelled = policy?.status === 'Cancelled' || !!policy?.cancellation;
  const availDocs = useMemo(() => availableDocs(isCancelled), [isCancelled]);
  const iframeRef = useRef(null);

  const [to, setTo] = useState(policy?.email || '');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [attach, setAttach] = useState(() => new Set());
  const [sending, setSending] = useState(false);

  // Load the template every time the composer opens or the template kind changes.
  useEffect(() => {
    if (!open) return;
    setTo(policy?.email || '');
    setCc(''); setBcc('');
    setSubject(initialSubject || '');
    setAttach(new Set((defaultAttachmentKeys || []).filter((k) => availDocs.some((d) => d.key === k))));
    // Give the iframe a tick to mount before writing.
    setTimeout(() => {
      const doc = iframeRef.current?.contentDocument;
      if (!doc) return;
      doc.open(); doc.write(initialHtml || ''); doc.close();
      try {
        doc.designMode = 'on';
        if (doc.body) doc.body.setAttribute('contenteditable', 'true');
      } catch {}
    }, 60);
  }, [open, templateKind, initialHtml, initialSubject, defaultAttachmentKeys, policy, availDocs]);

  const format = (cmd, value = null) => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    doc.execCommand(cmd, false, value);
    iframeRef.current.contentWindow.focus();
  };

  const insertLink = () => {
    const url = window.prompt('Enter the URL (e.g. https://…):');
    if (!url) return;
    format('createLink', url);
  };

  const toggleAttach = (k) => setAttach((prev) => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const send = async () => {
    const recipient = to.trim().toLowerCase();
    if (!isValidEmail(recipient)) return toast({ title: 'Please enter a valid recipient email' });
    const ccList = parseAddrs(cc);
    const bccList = parseAddrs(bcc);
    const badCc = ccList.find((e) => !isValidEmail(e));
    if (badCc) return toast({ title: `CC "${badCc}" is not a valid email` });
    const badBcc = bccList.find((e) => !isValidEmail(e));
    if (badBcc) return toast({ title: `BCC "${badBcc}" is not a valid email` });

    // Read the edited HTML + text out of the iframe.
    const doc = iframeRef.current?.contentDocument;
    const editedHtml = doc?.documentElement?.outerHTML || initialHtml;
    const editedText = doc?.body?.innerText || initialText || '';

    setSending(true);
    // Guard: if we don't have a token, tell the user BEFORE hitting the server.
    // Prevents the confusing "Token invalid or expired" response when the localStorage
    // session was cleared out-of-band (dev tools, browser cookie clear, etc.).
    const hasAuth = Object.keys(authHeaders()).length > 0;
    if (!hasAuth) {
      setSending(false);
      handleAuthFailure(toast);
      return;
    }
    try {
      let atts = [];
      if (attach.size > 0) {
        const built = await buildAttachments([...attach], policy?.planNumber, isCancelled);
        atts = built.attachments.map(({ size, ...a }) => a);
      }
      const res = await fetch(`${BACKEND}/api/emails/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          to: recipient,
          cc: ccList.length ? ccList : undefined,
          bcc: bccList.length ? bccList : undefined,
          subject: subject.trim() || 'Domestic Home Services',
          html: editedHtml,
          text: editedText,
          tag: tag || templateKind || 'compose',
          attachments: atts.length ? atts : undefined,
        }),
      });
      // Auth failure = 401. Treat this specifically so the operator understands
      // it's a login-expired issue and not a Resend outage.
      if (res.status === 401) {
        setSending(false);
        handleAuthFailure(toast);
        return;
      }
      const j = await res.json();
      if (res.ok && j.status === 'sent') {
        toast({ title: 'Email sent', description: `Delivered to ${recipient}${atts.length ? ` with ${atts.length} attachment${atts.length === 1 ? '' : 's'}` : ''}.` });
        onClose(true);
      } else if (res.ok && j.status === 'queued') {
        toast({ title: 'Queued', description: 'Resend is not configured — email queued for later delivery.' });
        onClose(true);
      } else {
        toast({ title: 'Send failed', description: j.detail || 'Please retry.' });
      }
    } catch (e) {
      toast({ title: 'Send failed', description: String(e?.message || e) });
    } finally { setSending(false); }
  };

  if (!open) return null;

  return (
    <div data-testid="email-composer" className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !sending && onClose()}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-6xl shadow-2xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#1F0068]/5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF5C39] font-bold">Preview & edit before sending</div>
            <h2 className="text-xl font-bold text-[#1F0068] mt-0.5 inline-flex items-center gap-2"><Mail className="w-4 h-4" /> Compose email — {templateKind}</h2>
          </div>
          <button onClick={() => !sending && onClose()} className="text-[#1F0068]/60 hover:text-[#1F0068]"><X className="w-5 h-5" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 grid md:grid-cols-3 gap-4">
          {/* Left column — recipients + attachments */}
          <div className="md:col-span-1 space-y-4">
            <div className="bg-[#FFF6EB] rounded-2xl p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold mb-2">Recipients</div>
              <Label className="text-xs text-[#1F0068]/70 font-semibold">To</Label>
              <Input data-testid="composer-to" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 mt-1 rounded-xl bg-white border-[#1F0068]/10 text-sm" />
              <Label className="text-xs text-[#1F0068]/70 font-semibold mt-3 block">CC</Label>
              <Input data-testid="composer-cc" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="Comma-separated" className="h-10 mt-1 rounded-xl bg-white border-[#1F0068]/10 text-sm" />
              <Label className="text-xs text-[#1F0068]/70 font-semibold mt-3 block">BCC</Label>
              <Input data-testid="composer-bcc" value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="Comma-separated" className="h-10 mt-1 rounded-xl bg-white border-[#1F0068]/10 text-sm" />
            </div>
            <div className="bg-[#FFF6EB] rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold inline-flex items-center gap-1"><Paperclip className="w-3 h-3" /> Attachments ({attach.size})</div>
                <div className="flex gap-2 text-[11px]">
                  <button type="button" onClick={() => setAttach(new Set(availDocs.map((d) => d.key)))} className="text-[#FF5C39] underline font-semibold">All</button>
                  <button type="button" onClick={() => setAttach(new Set())} className="text-[#1F0068]/70 underline font-semibold">None</button>
                </div>
              </div>
              <div className="space-y-1">
                {availDocs.map((doc) => {
                  const on = attach.has(doc.key);
                  return (
                    <label key={doc.key} data-testid={`composer-attach-${doc.key}`} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border cursor-pointer transition-colors ${on ? 'bg-white border-[#FF5C39]' : 'bg-white/50 border-transparent hover:border-[#1F0068]/20'}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleAttach(doc.key)} className="w-3.5 h-3.5 accent-[#FF5C39]" />
                      <FileText className="w-3.5 h-3.5 text-[#1F0068]/50" />
                      <span className="text-xs font-semibold text-[#1F0068] flex-1">{doc.label}</span>
                      {on && <CheckCircle2 className="w-3.5 h-3.5 text-[#FF5C39]" />}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right column — subject + WYSIWYG */}
          <div className="md:col-span-2 flex flex-col gap-3 min-h-[500px]">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Subject</Label>
              <Input data-testid="composer-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="h-11 mt-1 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10 font-semibold text-[#1F0068]" />
            </div>
            {/* Format toolbar */}
            <div className="flex items-center gap-1 bg-[#FFF6EB] rounded-full p-1 self-start">
              <button data-testid="composer-fmt-bold" type="button" onClick={() => format('bold')} title="Bold" className="w-9 h-9 rounded-full hover:bg-white flex items-center justify-center text-[#1F0068]"><Bold className="w-4 h-4" /></button>
              <button data-testid="composer-fmt-italic" type="button" onClick={() => format('italic')} title="Italic" className="w-9 h-9 rounded-full hover:bg-white flex items-center justify-center text-[#1F0068]"><Italic className="w-4 h-4" /></button>
              <button data-testid="composer-fmt-underline" type="button" onClick={() => format('underline')} title="Underline" className="w-9 h-9 rounded-full hover:bg-white flex items-center justify-center text-[#1F0068]"><Underline className="w-4 h-4" /></button>
              <div className="w-px h-5 bg-[#1F0068]/10 mx-1" />
              <button data-testid="composer-fmt-ul" type="button" onClick={() => format('insertUnorderedList')} title="Bulleted list" className="w-9 h-9 rounded-full hover:bg-white flex items-center justify-center text-[#1F0068]"><List className="w-4 h-4" /></button>
              <button data-testid="composer-fmt-link" type="button" onClick={insertLink} title="Insert link" className="w-9 h-9 rounded-full hover:bg-white flex items-center justify-center text-[#1F0068]"><LinkIcon className="w-4 h-4" /></button>
              <div className="w-px h-5 bg-[#1F0068]/10 mx-1" />
              <span className="text-[11px] text-[#1F0068]/60 pr-3">Click inside the email below to edit any text</span>
            </div>
            <iframe
              ref={iframeRef}
              data-testid="composer-editor"
              title="Email editor"
              className="w-full flex-1 min-h-[520px] rounded-xl bg-white border-2 border-[#FF5C39]/25"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-[#1F0068]/5 flex items-center justify-between gap-3">
          <p className="text-[11px] text-[#1F0068]/60 max-w-lg">Any edits you make here apply <b>only to this email</b>. The master template stays unchanged for future customers.</p>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onClose()} disabled={sending} className="rounded-full">Cancel</Button>
            <Button data-testid="composer-send" onClick={send} disabled={sending} className="rounded-full bg-[#00b67a] hover:bg-[#009a68] text-white gap-1.5 px-6">
              {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send Email{attach.size ? ` + ${attach.size} PDF${attach.size === 1 ? '' : 's'}` : ''}</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailComposer;
